import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth, isAdmin } from '@/lib/auth';
import { BrandLogo } from '@/lib/brand';
import { Mail, Lock, User as UserIcon, Building2, ArrowRight, AlertCircle } from 'lucide-react';

export default function Login() {
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const [mode, setMode] = useState(search.get('signup') ? 'signup' : 'login');
    const { login, register } = useAuth();
    const [form, setForm] = useState({
        email: '', password: '', full_name: '', department: '', region: '',
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        setErr(''); setBusy(true);
        try {
            const u = mode === 'login'
                ? await login(form.email, form.password)
                : await register({ ...form, role: 'trainee' });
            navigate(isAdmin(u) ? '/admin' : '/learn');
        } catch (e) {
            setErr(e?.response?.data?.detail || 'Authentication failed');
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
            {/* Left: brand panel */}
            <div className="hidden lg:flex relative bg-gradient-to-br from-brand-black via-[#3B3739] to-brand-black text-white p-12 flex-col justify-between overflow-hidden">
                <div className="absolute inset-0 grain-overlay" />
                {/* Yellow accent blob */}
                <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,242,0,0.35) 0%, transparent 70%)' }} />
                <div className="absolute -bottom-32 -left-32 h-96 w-96 rounded-full" style={{ background: 'radial-gradient(circle, rgba(227,30,36,0.18) 0%, transparent 70%)' }} />
                <div className="relative">
                    <Link to="/" data-testid="login-logo-link">
                        <BrandLogo dark />
                    </Link>
                </div>
                <div className="relative max-w-md">
                    <h2 className="font-display font-bold text-4xl xl:text-5xl leading-[1.1] text-balance">
                        Where India's <span style={{ color: '#FFF200' }}>cables &amp; wires industry</span> comes to learn.
                    </h2>
                    <p className="mt-5 text-slate-300 leading-relaxed">
                        AI-powered courses. Gamified arcade. Verifiable certifications.
                        Built exclusively for Propulse Cables &amp; Wires — Powering India's Future.
                    </p>
                </div>
                <div className="relative text-xs text-slate-400">
                    © Propulse Cables &amp; Wires · Powered by LearningHub.ai
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center p-6 lg:p-12">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-8">
                        <Link to="/"><BrandLogo /></Link>
                    </div>
                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-brand-red mb-2">
                        {mode === 'login' ? 'Welcome back' : 'New here?'}
                    </div>
                    <h1 className="font-display font-extrabold text-3xl lg:text-4xl text-slate-900 tracking-tight">
                        {mode === 'login' ? 'Sign in to the Academy.' : 'Create your learner account.'}
                    </h1>

                    {err && (
                        <div className="mt-5 flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
                            <AlertCircle className="h-4 w-4" /> {err}
                        </div>
                    )}

                    <form onSubmit={submit} className="mt-7 space-y-3" data-testid={mode === 'login' ? 'login-form' : 'signup-form'}>
                        {mode === 'signup' && (
                            <Field icon={UserIcon} label="Full Name" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testid="signup-fullname" />
                        )}
                        <Field icon={Mail} type="email" label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="login-email" />
                        <Field icon={Lock} type="password" label="Password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="login-password" />
                        {mode === 'signup' && (
                            <>
                                <Field icon={Building2} label="Department (optional)" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="signup-department" />
                                <Field icon={Building2} label="Region (optional)" value={form.region} onChange={(v) => setForm({ ...form, region: v })} testid="signup-region" />
                            </>
                        )}
                        <button
                            type="submit"
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl bg-brand-blue text-white font-semibold hover:bg-brand-blueDark disabled:opacity-60 shadow-lg shadow-brand-blue/20 mt-3"
                            data-testid={mode === 'login' ? 'login-submit-btn' : 'signup-submit-btn'}
                        >
                            {busy ? 'Please wait…' : (mode === 'login' ? 'Sign in' : 'Create account')}
                            <ArrowRight className="h-4 w-4" />
                        </button>
                    </form>

                    <button
                        onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
                        className="mt-4 text-sm text-slate-600 hover:text-brand-blue"
                        data-testid="toggle-mode-btn"
                    >
                        {mode === 'login' ? 'New to the Academy? Create a learner account →' : 'Already have an account? Sign in →'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ icon: Icon, label, type = 'text', value, onChange, testid }) {
    return (
        <label className="block">
            <span className="text-xs font-semibold text-slate-700">{label}</span>
            <div className="mt-1 relative">
                <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    required={!label.includes('optional')}
                    className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 text-sm"
                    data-testid={testid}
                    data-testid-alt={type === 'email' ? 'login-email-input' : (type === 'password' ? 'login-password-input' : undefined)}
                />
            </div>
        </label>
    );
}
